/**
 * Financial Summary Component
 * Displays totals, discounts, and tax breakdown
 * AC-2: Financial Summary
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CurrencyFormatter } from '@/components/common/CurrencyFormatter';
import { FinancialSummary as FinancialSummaryType } from '@/types';
import { Separator } from '@/components/ui/separator';

interface FinancialSummaryProps {
  financialSummary: FinancialSummaryType;
}

export const FinancialSummary = ({ financialSummary }: FinancialSummaryProps) => {
  const {
    subtotal,
    totalDiscount,
    cgstAmount,
    sgstAmount,
    igstAmount,
    cessAmount,
    totalTax,
    totalAmount,
    amountPaid,
    balanceDue,
  } = financialSummary;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Financial Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Subtotal */}
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Subtotal</span>
            <CurrencyFormatter amount={subtotal} className="font-medium" />
          </div>

          {/* Discount */}
          {totalDiscount > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Total Discount</span>
              <CurrencyFormatter
                amount={-totalDiscount}
                className="font-medium text-green-600"
              />
            </div>
          )}

          <Separator />

          {/* Tax Breakdown */}
          <div className="space-y-2">
            <p className="text-sm font-medium">GST Breakdown</p>
            {cgstAmount > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground pl-4">CGST</span>
                <CurrencyFormatter amount={cgstAmount} />
              </div>
            )}
            {sgstAmount > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground pl-4">SGST</span>
                <CurrencyFormatter amount={sgstAmount} />
              </div>
            )}
            {igstAmount > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground pl-4">IGST</span>
                <CurrencyFormatter amount={igstAmount} />
              </div>
            )}
            {cessAmount > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground pl-4">CESS</span>
                <CurrencyFormatter amount={cessAmount} />
              </div>
            )}
            <div className="flex justify-between items-center font-medium">
              <span className="text-muted-foreground">Total Tax</span>
              <CurrencyFormatter amount={totalTax} />
            </div>
          </div>

          <Separator />

          {/* Total Amount */}
          <div className="flex justify-between items-center text-lg font-bold">
            <span>Total Amount</span>
            <CurrencyFormatter amount={totalAmount} />
          </div>

          {/* Payment Status */}
          {amountPaid > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Amount Paid</span>
                  <CurrencyFormatter
                    amount={amountPaid}
                    className="font-medium text-green-600"
                  />
                </div>
                <div className="flex justify-between items-center font-bold text-lg">
                  <span>Balance Due</span>
                  <CurrencyFormatter
                    amount={balanceDue}
                    className={balanceDue > 0 ? 'text-red-600' : 'text-green-600'}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
